import { StatsigEvent } from '@/tracking/statsig/types';
import { pushStatsig } from '@/tracking/statsig/index';
import {
    AnyBookingProduct,
    CouponPlacement,
    CouponType,
    FlightProduct,
    InformationType,
    Money,
    MyBookingSpotlight,
    ProductType,
    SpotlightType,
} from '@/api/MyBooking/myBooking.interfaces';
import { mapBookingProductsAsItems } from '../analytics/MyBooking/Helpers/map';
import {
    AnyUpsellAddon,
    UpSellAddonFerry,
    UpSellAddonServicePackage,
    UpSellType,
} from '@/api/MyBookingSession/myBookingSession.interfaces';
import { HotelWidgetItem } from '@ui/components/HotelCard/HotelCard.interfaces';
import { TripWidgetData } from '@/components/myBookingPage/Sections/Widgets/Trip/TripWidget.interfaces';
import { WidgetParams } from '@/components/myBookingPage/Sections/UpSell/Modals/Variants/RentalCar/RentalCar.interfaces';
import { sum } from 'lodash';
import { MyBookingCreated, MyBookingPaymentStatus } from '@/store/myBooking/types';
import { getDurationInDays } from '@shared/helpers/datetime/datetime';
import { Dictionary } from '@shared/interfaces/dictionary';
import { bookingProductHasRyanair, getFlightRouteSummary } from '@/helpers/myBooking';
import store from '@/store';
import groupBy from 'lodash/groupBy';
import {
    isConfirmed,
    isExpired,
    isLoading,
} from '@/components/myBookingPage/Modals/VerificationRyanair/VerificationRyanair.helpers';

const getProductsType = (productArray: AnyBookingProduct[]): Record<string, number> => {
    return productArray.reduce(
        (acc, product) => {
            const type = `count_${product.type?.toLowerCase()}`;
            acc[type] = (acc[type] || 0) + 1;
            return acc;
        },
        {} as Record<string, number>
    );
};

const getProductsRyanair = (products: FlightProduct[]) => {
    const flights = products.filter((p) => bookingProductHasRyanair(p));

    const flightsLoading = flights.filter((p) => isLoading(p.ticket?.verification));
    const flightsConfirmed = flights.filter((p) => isConfirmed(p.ticket?.verification));
    const flightsExpired = flights.filter((p) => isExpired(p.ticket?.verification));

    return {
        count_flight_ryanair: flights.length,
        count_flight_ryanair_loading: flightsLoading.length,
        count_flight_ryanair_verified: flightsConfirmed.length,
        count_flight_ryanair_expired: flightsExpired.length,
    };
};

const getProductsBrandedFare = (flights: FlightProduct[]) => {
    const branded = flights.filter((p) => p.flightBrandedFare);
    const names =
        branded.length > 0 ? branded.map((x) => x.flightBrandedFare?.brandName) : [];

    return {
        count_flight_branded_fares: branded.length,
        count_flight_branded_fares_names: names,
    };
};

const getProductBrandedFare = (product: FlightProduct) => {
    const branded = product?.flightBrandedFare;
    if (!branded) return {};

    const route = getFlightRouteSummary(product?.legs);
    const attributes = branded?.attributes ?? [];

    return {
        flight_branded_fare_name: branded?.brandName,
        flight_branded_fare_carrier: product?.legs[0]?.platingCarrier,
        flight_branded_fare_route: route?.label,
        flight_branded_fare_route_from: route?.from,
        flight_branded_fare_route_to: route?.to,
        flight_branded_fare_attributes: attributes.length ?? 0,
        flight_branded_fare_attribute_types: attributes.map((x) => x.type) ?? [],
        flight_branded_fare_attribute_status: attributes.map((x) => x.status) ?? [],
    };
};

const getPaymentStatusData = (payment: MyBookingPaymentStatus) => {
    return {
        down_payment_due_hours: payment.downPayment?.hours,
        final_payment_due_hours: payment.finalPayment?.hours,
    };
};

const getCreatedData = (created: MyBookingCreated) => {
    const hour = created.time?.split(':')[0];
    const days = getDurationInDays(new Date(), created.date);

    return {
        booking_created_days_ago: days,
        booking_created_hour: hour,
        booking_created_time: created.time,
        booking_created_date: created.shortDate,
    };
};

const getHotelWidgetItemData = (item: HotelWidgetItem, position: number) => {
    return {
        widget_hotel_position: position,
        widget_hotel_code: item.hotelCode,
        widget_hotel_title: item.title,
        widget_hotel_rating: item.rating,
        widget_hotel_tag: item.tag,
        widget_hotel_adults: item.summary.adults,
        widget_hotel_children: item.summary.children.length,
        widget_hotel_nights: item.summary.nights,
    };
};

const getTripWidgetData = (data: TripWidgetData) => {
    return {
        widget_trip_id: data.trip.code,
        widget_trip_type: data.trip.type,
        widget_trip_adults: data.passengers.adults,
        widget_trip_children: data.passengers.children?.length,
        widget_trip_stops_count: data.trip.stops?.length,
        widget_trip_stops: data.trip.stops.map((x) => x.code),
        widget_trip_nights: sum(data.trip.stops.map((x) => x.nights)),
    };
};

const getSpotlightPositionData = (type: SpotlightType) => {
    const position = store.getters['myBooking/getSpotlightPositionByType'](type);
    return { spotlight_position: position };
};

const getRentalCarWidgetData = (params: WidgetParams) => {
    return {
        widget_rentalcar_age: params.driversAge,
        widget_rentalcar_dropoff: params.dropOff?.airportName,
        widget_rentalcar_dropoff_iata: params.dropOff?.iata,
        widget_rentalcar_pickup: params.pickUp?.airportName,
        widget_rentalcar_pickup_iata: params.pickUp?.iata,
        widget_rentalcar_locale: params.locale,
    };
};

export const logStatsigBookingPageInitiated = (
    products: AnyBookingProduct[],
    addons: AnyUpsellAddon[],
    totalBookingValue: Money,
    created: MyBookingCreated,
    isLoggedIn: boolean
) => {
    const flights = [...products].filter((x) => x.type === ProductType.Flight);

    const metadata = {
        currency: totalBookingValue.currency,
        value: totalBookingValue.amount?.toString(),
        items: mapBookingProductsAsItems(products),
        ...getProductsType(products),
        ...getProductsRyanair(flights),
        ...getProductsBrandedFare(flights),
        ...getCreatedData(created),
        ...getUpSellAddons(addons),
        isLoggedIn,
    };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_PAGE_INITIATED,
        metadata,
    });
};

export const logStatsigBookingBrandedFareSelected = (product: FlightProduct) => {
    if (!product.flightBrandedFare) return;

    const metadata = {
        ...getProductBrandedFare(product),
    };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_BRANDED_FARE_SELECTED,
        metadata,
    });
};

export const logStatsigBookingRyanairVerification = (products: AnyBookingProduct[]) => {
    const flights = [...products].filter((x) => x.type === ProductType.Flight);

    const metadata = {
        ...getProductsRyanair(flights),
    };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_RYANAIR_VERIFICATION,
        metadata,
    });
};

export const logStatsigBookingRyanairInteraction = (
    interactionType: 'VerifyBooking' | 'ExternalUpsell'
) => {
    const metadata = {
        interactionType,
    };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_RYANAIR_INTERACTION,
        metadata,
    });
};

export const logStatsigBookingRyanairBookingExpired = (products: FlightProduct[]) => {
    const metadata = {
        count_flight_ryanair_expired: products.length,
    };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_RYANAIR_BOOKING_EXPIRED,
        metadata,
    });
};

export const logStatsigBookingHotelWidgetAddonCompleted = (
    hotel: HotelWidgetItem,
    position: number,
    available: number
) => {
    const metadata = {
        widget_available_hotels: available,
        ...getHotelWidgetItemData(hotel, position),
        ...getSpotlightPositionData(SpotlightType.HotelWidget),
    };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_HOTEL_WIDGET_ADDON,
        metadata,
    });
};

export const logStatsigBookingTripWidgetAddonCompleted = (data: TripWidgetData) => {
    const metadata = {
        ...getTripWidgetData(data),
        ...getSpotlightPositionData(SpotlightType.TripWidget),
    };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_TRIP_WIDGET_ADDON,
        metadata,
    });
};

export const logStatsigBookingRentalCarWidgetAddonCompleted = (params: WidgetParams) => {
    const metadata = {
        ...getRentalCarWidgetData(params),
        ...getSpotlightPositionData(SpotlightType.RentalCarWidget),
    };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_RENTALCAR_WIDGET_ADDON,
        metadata,
    });
};

export const logStatsigBookingAccommodationAddonCompleted = () => {
    const metadata = {
        ...getSpotlightPositionData(SpotlightType.Upsells),
    };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_ACCOMMODATION_ADDON,
        metadata,
    });
};

export const logStatsigBookingBaggageAddonCompleted = () => {
    const metadata = {
        ...getSpotlightPositionData(SpotlightType.Upsells),
    };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_BAGGAGE_ADDON,
        metadata,
    });
};

export const logStatsigBookingRentalCarAddonCompleted = () => {
    const metadata = {
        ...getSpotlightPositionData(SpotlightType.Upsells),
    };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_RENTALCAR_ADDON,
        metadata,
    });
};

export const logStatsigBookingTransferAddonCompleted = () => {
    const metadata = {
        ...getSpotlightPositionData(SpotlightType.Upsells),
    };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_TRANSFER_ADDON,
        metadata,
    });
};

export const logStatsigBookingSeatingAddonCompleted = () => {
    const metadata = {
        ...getSpotlightPositionData(SpotlightType.Upsells),
    };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_SEATING_ADDON,
        metadata,
    });
};

export const logStatsigBookingTravelInsuranceAddonCompleted = () => {
    const metadata = {
        ...getSpotlightPositionData(SpotlightType.Upsells),
    };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_TRAVELINSURANCE_ADDON,
        metadata,
    });
};

export const logStatsigBookingFerryExtraAddonCompleted = (addons: UpSellAddonFerry[]) => {
    const metadata = {
        ...getSpotlightPositionData(SpotlightType.Upsells),
        ...getAddonExtraData(addons),
    };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_FERRY_EXTRA_ADDON,
        metadata,
    });
};

export const logStatsigBookingAddonOpen = (
    addonType: UpSellType,
    addonPlacement: 'UpSell' | 'Section'
) => {
    const metadata = {
        addonType,
        addonPlacement,
        ...(addonPlacement === 'UpSell' && getSpotlightPositionData(SpotlightType.Upsells)),
    };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_ADDON_OPEN,
        metadata,
    });
};

export const logStatsigBookingPaymentModal = (
    payment: MyBookingPaymentStatus,
    products: AnyBookingProduct[],
    totalBookingValue: Money
) => {
    const metadata = {
        ...getPaymentStatusData(payment),
        value: totalBookingValue.amount?.toString(),
        items: mapBookingProductsAsItems(products),
        ...getProductsType(products),
    };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_PAYMENT_MODAL,
        metadata,
    });
};

export const logStatsigBookingPaymentModalInteraction = (
    interactionType: 'Pay' | 'View',
    payment: MyBookingPaymentStatus,
    products: AnyBookingProduct[],
    totalBookingValue: Money
) => {
    const metadata = {
        interactionType,
        value: totalBookingValue.amount?.toString(),
        items: mapBookingProductsAsItems(products),
        ...getProductsType(products),
        ...getPaymentStatusData(payment),
    };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_PAYMENT_MODAL_INTERACTION,
        metadata,
    });
};

export const logStatsigBookingPaymentRedirect = (products: AnyBookingProduct[]) => {
    const metadata = {
        items: mapBookingProductsAsItems(products),
        ...getProductsType(products),
    };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_PAYMENT_REDIRECT,
        metadata,
    });
};

export const logStatsigBookingHotelWidgetAvailable = (
    hotel: HotelWidgetItem,
    position: number
) => {
    const metadata = {
        ...getHotelWidgetItemData(hotel, position),
    };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_HOTEL_WIDGET_AVAILABLE,
        metadata,
    });
};

export const logStatsigBookingSpotlights = (spotlights: MyBookingSpotlight[]) => {
    const types = spotlights.map((x) => x.type);
    const metadata = { spotlights_types: types };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_SPOTLIGHTS,
        metadata,
    });
};

export const logStatsigBookingSpotlightsRearranged = (removed: SpotlightType[]) => {
    const metadata = {
        spotlights_removed: removed,
    };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_SPOTLIGHTS_REARRANGED,
        metadata,
    });
};

export const logStatsigBookingTripWidgetAvailable = (data: TripWidgetData) => {
    const metadata = {
        ...getTripWidgetData(data),
        ...getSpotlightPositionData(SpotlightType.TripWidget),
    };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_TRIP_WIDGET_AVAILABLE,
        metadata,
    });
};

export const logStatsigBookingHotelWidgetInteraction = (
    interactionType: 'Swipe' | 'MoreStays' | 'PriceGuarantee'
) => {
    const metadata = {
        interactionType,
        ...getSpotlightPositionData(SpotlightType.HotelWidget),
    };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_HOTEL_WIDGET_INTERACTION,
        metadata,
    });
};

export const logStatsigBookingHotelWidgetEmptyResult = (destination: string) => {
    const metadata = {
        widget_destination: destination,
        ...getSpotlightPositionData(SpotlightType.HotelWidget),
    };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_HOTEL_WIDGET_EMPTY_RESULT,
        metadata,
    });
};

export const logStatsigBookingUpsellInteraction = (interactionType: 'Swipe' | 'ShowMore') => {
    const metadata = {
        interactionType,
        ...getSpotlightPositionData(SpotlightType.Upsells),
    };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_UPSELL_INTERACTION,
        metadata,
    });
};

export const logStatsigBookingPageInteraction = (interactionType: 'ShowPackageRules') => {
    const metadata = {
        interactionType,
    };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_PAGE_INTERACTION,
        metadata,
    });
};

export const logStatsigBookingCancellationTermsClick = (product: AnyBookingProduct) => {
    const metadata = {
        type: product.type,
    };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_CANCELLATION_TERMS_CLICK,
        metadata,
    });
};

export const logStatsigBookingTransferConditionsClick = (product: AnyBookingProduct) => {
    const metadata = {
        type: product.type,
    };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_TRANSFER_CONDITIONS_CLICK,
        metadata,
    });
};

export const logStatsigBookingTransferVoucherClick = (product: AnyBookingProduct) => {
    const metadata = {
        type: product.type,
    };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_TRANSFER_VOUCHER_CLICK,
        metadata,
    });
};

export const logStatsigBookingUpsellAvailable = (addonType: UpSellType) => {
    const metadata = {
        addonType,
        ...getSpotlightPositionData(SpotlightType.Upsells),
    };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_UPSELL_AVAILABLE,
        metadata,
    });
};

export const logStatsigBookingServicePackageAvailable = (
    addon: UpSellAddonServicePackage
) => {
    const metadata = {
        addonType: addon.Type,
        servicePackageType: addon.PackageType,
    };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_SERVICE_PACKAGE_AVAILABLE,
        metadata,
    });
};

export const logStatsigBookingServicePackageInteraction = (
    interactionType: 'Select' | 'UnSelect' | 'ReadMore',
    addon?: UpSellAddonServicePackage
) => {
    const metadata = {
        addonType: addon?.Type,
        servicePackageType: addon?.PackageType,
        interactionType,
    };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_SERVICE_PACKAGE_INTERACTION,
        metadata,
    });
};

export const logStatsigServicePackageOpen = (addonPlacement: 'Highlight' | 'Section') => {
    const metadata = {
        addonPlacement,
    };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_SERVICE_PACKAGE_OPEN,
        metadata,
    });
};

export const logStatsigServicePackageClose = () => {
    pushStatsig({
        eventName: StatsigEvent.BOOKING_SERVICE_PACKAGE_CLOSE,
    });
};

export const logStatsigBookingServicePackageAddonCompleted = (
    addon: UpSellAddonServicePackage
) => {
    const metadata = {
        addonType: addon.Type,
        servicePackageType: addon.PackageType,
    };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_SERVICEPACKAGE_ADDON,
        metadata,
    });
};

export const logStatsigBookingCancelClick = (products: AnyBookingProduct[]) => {
    const metadata = {
        ...getProductsType(products),
    };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_CANCEL_CLICK,
        metadata,
    });
};

export const logStatsigBookingConfirmationClick = (products: AnyBookingProduct[]) => {
    const metadata = {
        ...getProductsType(products),
    };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_CONFIRMATION_CLICK,
        metadata,
    });
};

export const logStatsigBookingVoucherClick = (products: AnyBookingProduct[]) => {
    const metadata = {
        ...getProductsType(products),
    };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_VOUCHER_CLICK,
        metadata,
    });
};

export const logStatsigBookingPayClick = (
    products: AnyBookingProduct[],
    totalBookingValue: Money,
    placement: 'Default' | 'Highlight'
) => {
    const metadata = {
        pay_button_placement: placement,
        value: totalBookingValue.amount?.toString(),
        items: mapBookingProductsAsItems(products),
        ...getProductsType(products),
    };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_PAY_CLICK,
        metadata,
    });
};

export const logStatsigBookingFooterNewsletterClick = () => {
    pushStatsig({
        eventName: StatsigEvent.BOOKING_FOOTER_NEWSLETTER_CLICK,
    });
};

export const logStatsigBookingFooterFaqClick = () => {
    pushStatsig({
        eventName: StatsigEvent.BOOKING_FOOTER_FAQ_CLICK,
    });
};

export const logStatsigBookingImportantInformationOpen = (type: InformationType) => {
    const metadata = { information_type: type };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_IMPORTANT_INFORMATION_OPEN,
        metadata,
    });
};

export const logStatsigBookingOpenSectionJourneyDetails = (type: 'Flight' | 'Ferry') => {
    const metadata = { type };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_OPEN_SECTION_JOURNEY_DETAILS,
        metadata,
    });
};

export const logStatsigBookingOpenSectionPassengersDetails = (type: 'Flight' | 'Ferry') => {
    const metadata = { type };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_OPEN_SECTION_PASSENGERS_DETAILS,
        metadata,
    });
};

export const logStatsigBookingServicePackageRequestOpen = () => {
    pushStatsig({
        eventName: StatsigEvent.BOOKING_SERVICE_PACKAGE_REQUEST_OPEN,
    });
};

export const logStatsigBookingServicePackageRequestSent = (category: string) => {
    const metadata = { message_category: category };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_SERVICE_PACKAGE_REQUEST_SENT,
        metadata,
    });
};

export const logStatsigBookingCustomerServiceMessagesOpen = (count: number) => {
    const metadata = { count_customer_service_messages: count };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_CUSTOMER_SERVICE_MESSAGES_OPEN,
        metadata,
    });
};

export const logStatsigBookingPackageRulesPrompted = (addonType: UpSellType | undefined) => {
    const metadata = { addonType };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_PACKAGE_RULES_PROMPTED,
        metadata,
    });
};

export const logStatsigBookingPackageRulesContinued = (addonType: UpSellType | undefined) => {
    const metadata = { addonType };

    pushStatsig({
        eventName: StatsigEvent.BOOKING_PACKAGE_RULES_CONTINUED,
        metadata,
    });
};

export const logStatsigBookingCouponVisible = () => {
    pushStatsig({
        eventName: StatsigEvent.BOOKING_COUPON_VISIBLE,
    });
};

export const logStatsigBookingCouponOpen = (
    coupon_type: CouponType,
    coupon_placement: CouponPlacement,
    isClipboardSupported: boolean
) => {
    const metadata = { coupon_type, coupon_placement, isClipboardSupported };
    pushStatsig({
        eventName: StatsigEvent.BOOKING_COUPON_OPEN,
        metadata,
    });
};

export const logStatsigBookingCouponClose = () => {
    pushStatsig({
        eventName: StatsigEvent.BOOKING_COUPON_CLOSE,
    });
};

export const logStatsigBookingCouponCodeCopied = (
    coupon_type: CouponType,
    coupon_placement: CouponPlacement
) => {
    const metadata = { coupon_type, coupon_placement };
    pushStatsig({
        eventName: StatsigEvent.BOOKING_COUPON_CODE_COPIED,
        metadata,
    });
};

export const logStatsigAddRemoveFerryAddon = (
    addon: AnyUpsellAddon,
    event: StatsigEvent.BOOKING_ADD_ADDON | StatsigEvent.BOOKING_REMOVE_ADDON
) => {
    pushStatsig({
        eventName: event,
    });
};

const getUpSellAddons = (addons: AnyUpsellAddon[]) => {
    const addonGroups = groupBy(addons, 'Type');
    const dictionary: Dictionary<string | number> = {};
    Object.entries(addonGroups).forEach(([type, values]) => {
        const [addon] = values;
        const key = `${addon.ProductType}_${type}_quantity`.toLowerCase();
        dictionary[key] = values.length;
    });
    return dictionary;
};

const getAddonExtraData = (addons: UpSellAddonFerry[]) => {
    const addonGroups = groupBy(addons, 'Type');
    const dictionary: Dictionary<string | number> = {};
    Object.entries(addonGroups).forEach(([type, values]) => {
        const key = `ferry_${type}`.toLowerCase();
        dictionary[`${key}_quantity`] = values.length;
        dictionary[`${key}_price`] = values.reduce(
            (total, current) => total + (current.Price?.Amount ?? 0) * (current?.Value ?? 0),
            0
        );
    });
    return dictionary;
};

export const logStatsigBookingSupplierNotes = () => {
    pushStatsig({
        eventName: StatsigEvent.BOOKING_OPEN_SUPPLIER_NOTES,
    });
};
